import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flame, Image as ImageIcon, RefreshCw, Share2, Smartphone, X } from 'lucide-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import TitleSectionBack from 'Containers/Common/TitleSectionBack';
import { getActiveSalon, getActiveSalonId } from 'Containers/Auth/store/selectors';
import {
  getCalendarLayoutSource,
  getCalendarSlots,
  getCalendarWorkerServices,
} from 'Containers/Scheduler/store/selectors';
import { schedulerActions } from 'Containers/Scheduler/store';
import { getReviews } from 'Containers/Reviews/store/selectors';
import { reviewsActions } from 'Containers/Reviews/store';
import { getIconUrl, imageEntities } from 'utils/ImagesCreator';
import useWindowSize from 'utils/useWindowSize';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { toastActions } from 'Containers/Toasts/store';
import { modalActions } from 'Containers/Modals/store';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import {
  CATEGORIES,
  getAllTemplates,
  pickVisibleTemplates,
  REGENERATE_SECTION_IDS,
  resolveTemplateAccess,
} from '../../catalog';
import {
  HeadlineStoryCard,
  SlotsStoryCard,
  FunnyStoryCard,
  ReviewStoryCard,
  QrStoryCard,
  PromoStoryCard,
  SeasonalStoryCard,
  useStoryShare,
} from '../../storyKit';
import styles from './AdvertisingHub.module.scss';

const SKIP_LOADER = { skipLoader: true };
const EMPTY_ARR = [];

const getWorkerName = (w) => w?.nome_referente || w?.nome || w?.name || '';

const useShareToast = (t) => {
  const dispatch = useDispatch();
  return useCallback(
    (kind) => {
      if (kind === 'error') {
        dispatch(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'worker',
            props: {
              title: t('scheduler.workerSoldOutModal.shareError'),
              warning: true,
            },
          }),
        );
        return;
      }
      const title =
        kind === 'retry'
          ? t('scheduler.workerSoldOutModal.shareRetryHint')
          : kind === 'photos'
            ? t('scheduler.workerSoldOutModal.shareSaveToPhotosHint')
            : t('scheduler.workerSoldOutModal.shareSavedFallback');
      dispatch(
        toastActions.OPEN_TOAST.SUCCESS({
          type: 'worker',
          props: {
            title,
            warning: false,
          },
        }),
      );
    },
    [dispatch, t],
  );
};

const getTemplateLabel = (template, t) => {
  if (template.kind === 'funny') {
    return t(`advertising.funnyPhrases.${template.phraseId}`);
  }
  if (template.kind === 'qr') return t('advertising.templates.qr');
  if (template.kind === 'generateChair') {
    return t(`home.storiesProposals.types.${template.proposalType}`, {
      defaultValue: t('advertising.templates.generateChair'),
    });
  }
  if (template.kind === 'generateSlots') return t('advertising.templates.generateSlots');
  if (template.kind === 'generateReview') return t('advertising.templates.generateReview');
  if (template.kind === 'generatePromo') return t('advertising.templates.generatePromo');
  if (template.kind === 'reviewTeaser') return t('advertising.templates.reviewTeaser');
  if (template.kind === 'promoTeaser') return t('advertising.templates.promoTeaser');
  if (template.kind === 'seasonal') {
    return t(`advertising.seasonal.${template.seasonalId}.label`);
  }
  if (template.proposalType) {
    return t(`home.storiesProposals.types.${template.proposalType}`, {
      defaultValue: template.proposalType,
    });
  }
  return template.id;
};

/** Anteprima card per il catalogo (dati salone; i generatori mostrano un esempio). */
const TemplatePreview = ({
  template,
  salon,
  captureRef,
  square,
  review,
  promo,
  worker,
  slots,
  slotsDate,
  t,
}) => {
  if (template.kind === 'qr') {
    return <QrStoryCard captureRef={captureRef} salon={salon} square={square} t={t} />;
  }
  if (template.kind === 'funny') {
    return (
      <FunnyStoryCard
        captureRef={captureRef}
        salon={salon}
        phrase={t(`advertising.funnyPhrases.${template.phraseId}`)}
        square={square}
        t={t}
      />
    );
  }
  if (template.kind === 'seasonal') {
    return (
      <SeasonalStoryCard
        captureRef={captureRef}
        salon={salon}
        seasonalId={template.seasonalId}
        square={square}
        t={t}
      />
    );
  }
  if (template.kind === 'generateSlots') {
    return (
      <SlotsStoryCard
        captureRef={captureRef}
        workerName={worker?.name || salon?.name || salon?.nome_salone || ''}
        workerIcon={worker?.icon || salon?.icon}
        date={slotsDate || dayjs().format('YYYY-MM-DD')}
        slots={
          slots && slots.length
            ? slots
            : [
                { oraInizio: '10:00', oraFine: '10:30' },
                { oraInizio: '11:30', oraFine: '12:00' },
                { oraInizio: '15:00', oraFine: '15:30' },
                { oraInizio: '17:30', oraFine: '18:00' },
              ]
        }
        square={square}
        t={t}
      />
    );
  }
  if (template.kind === 'generatePromo' || template.kind === 'promoTeaser') {
    return (
      <PromoStoryCard
        captureRef={captureRef}
        salon={salon}
        title={promo?.title || t('advertising.promo.sampleTitle')}
        discount={promo?.discount || '-20%'}
        validity={promo?.validity || t('advertising.promo.sampleValidity')}
        square={square}
        t={t}
      />
    );
  }
  if (template.kind === 'generateReview' || template.kind === 'reviewTeaser' || review) {
    return (
      <ReviewStoryCard
        captureRef={captureRef}
        salon={salon}
        review={
          review || {
            rating_average: 5,
            testo: t('advertising.reviews.sampleText'),
            nome: t('advertising.reviews.sampleName'),
          }
        }
        square={square}
        t={t}
      />
    );
  }
  return (
    <HeadlineStoryCard
      captureRef={captureRef}
      salon={salon}
      workerName={worker?.name}
      workerIcon={worker?.icon}
      proposalType={template.proposalType || 'salon'}
      square={square}
      t={t}
    />
  );
};

const TemplateTile = memo(function TemplateTile({
  template,
  salon,
  access,
  format,
  isMobile,
  t,
  onUpgrade,
  onOpenGenerator,
  onSharedToast,
}) {
  // Niente prefetch: in catalogo ci sono decine di tile e html2canvas in parallelo
  // blocca il main thread (pagina Pubblicità inchiodata).
  const { captureRef, ensureFile, share, sharing } = useStoryShare({
    fileBaseName: `zetabarber-${template.id}-${format}`,
    format,
    isMobile,
    onToast: onSharedToast,
    prefetch: false,
  });

  const locked = access === 'teaser';
  const needsGenerator =
    !locked &&
    (template.kind === 'generateChair' ||
      template.kind === 'generateSlots' ||
      template.kind === 'generateReview' ||
      template.kind === 'generatePromo');

  const handleClick = () => {
    if (locked) {
      onUpgrade();
      return;
    }
    if (needsGenerator) {
      onOpenGenerator(template);
      return;
    }
    share();
  };

  return (
    <article className={styles.tile}>
      <div className={styles.phone_frame}>
        <TemplatePreview
          template={template}
          salon={salon}
          captureRef={captureRef}
          square={format === 'post'}
          t={t}
        />
        {locked ? (
          <div className={styles.teaser_overlay}>
            <button type="button" className={styles.tile_upgrade} onClick={onUpgrade}>
              <Flame size={14} strokeWidth={2.4} fill="currentColor" aria-hidden />
              <span>{t('advertising.teaser.cta')}</span>
            </button>
          </div>
        ) : null}
      </div>
      <p className={styles.tile_label}>{getTemplateLabel(template, t)}</p>
      {locked ? (
        <button type="button" className={styles.share_btn_locked} onClick={onUpgrade}>
          <Flame size={16} strokeWidth={2.25} fill="currentColor" aria-hidden />
          <span>{t('advertising.teaser.cta')}</span>
        </button>
      ) : (
        <button
          type="button"
          className={styles.share_btn}
          disabled={sharing}
          onPointerDown={() => {
            if (!needsGenerator) ensureFile().catch(() => {});
          }}
          onClick={handleClick}
        >
          <Share2 size={16} strokeWidth={2.25} aria-hidden />
          <span>
            {sharing
              ? t('scheduler.workerSoldOutModal.preparing')
              : needsGenerator
                ? t('advertising.actions.customize')
                : t('advertising.actions.share')}
          </span>
        </button>
      )}
    </article>
  );
});

/** Sceglie il servizio di default per una poltrona (stessa logica di WorkerSoldOutModal). */
const pickDefaultService = (services, workerId) => {
  let find = (services || []).filter(
    (s) => +s.worker_id === +workerId && s.durata <= 30 && s.disabilitato == 0,
  );
  if (find.length === 0) {
    find = (services || []).filter(
      (s) => +s.worker_id === +workerId && s.durata > 30 && s.disabilitato == 0,
    );
  }
  return [...find].sort((a, b) => b.durata - a.durata)[0];
};

const GeneratorModal = ({
  template,
  salon,
  workers,
  workerIconById,
  reviews,
  format,
  isMobile,
  t,
  onClose,
  onSharedToast,
}) => {
  const dispatch = useDispatch();
  // Selector memoizzati una volta: le factory ritornano array nuovi a ogni render
  // e in dipendenza di un effect causerebbero un loop di dispatch.
  const servicesSelector = useMemo(() => getCalendarWorkerServices(), []);
  const slotsSelector = useMemo(() => getCalendarSlots(), []);
  const services = useSelector(servicesSelector) || EMPTY_ARR;
  const availableSlots = useSelector(slotsSelector) || EMPTY_ARR;

  const [workerId, setWorkerId] = useState(workers?.[0]?.idpoltrona ?? '');
  const [slotsDay, setSlotsDay] = useState('');
  const [reviewId, setReviewId] = useState(null);
  const [promoTitle, setPromoTitle] = useState('');
  const [promoDiscount, setPromoDiscount] = useState('-20%');
  const [promoValidity, setPromoValidity] = useState('');

  const isSlots = template.kind === 'generateSlots';
  const isChair = template.kind === 'generateChair';
  const isReview = template.kind === 'generateReview';
  const isPromo = template.kind === 'generatePromo';

  const selectedWorker = useMemo(
    () => (workers || []).find((w) => String(w.idpoltrona) === String(workerId)),
    [workerId, workers],
  );
  // Solo recensioni con testo e almeno 4 stelle (ultime 5)
  const usableReviews = useMemo(
    () =>
      (reviews || [])
        .filter((r) => r.testo && r.testo.trim() && Number(r.rating_average) >= 4)
        .slice(0, 5),
    [reviews],
  );
  const selectedReview = useMemo(
    () =>
      usableReviews.find((r) => r.id_recensione === reviewId) || usableReviews[0],
    [reviewId, usableReviews],
  );

  // Slot liberi reali per la poltrona scelta (come WorkerSoldOutModal).
  // Una sola richiesta per poltrona: il ref evita re-dispatch quando lo store cambia.
  const requestedWorkerRef = useRef(null);
  useEffect(() => {
    if (!isSlots || !workerId) return;
    if (requestedWorkerRef.current === workerId) return;
    const svc = pickDefaultService(services, workerId);
    if (!svc) return;
    requestedWorkerRef.current = workerId;
    dispatch(schedulerActions.GET_CALENDAR_SLOTS.CLEAR());
    dispatch(
      schedulerActions.GET_CALENDAR_SLOTS.REQUEST({
        servizi: [svc],
        idPoltrona: workerId,
        nGiorniPrenotabili: 15,
        lang: 'it',
      }),
    );
  }, [dispatch, isSlots, services, workerId]);

  useEffect(
    () => () => {
      if (isSlots) dispatch(schedulerActions.GET_CALENDAR_SLOTS.CLEAR());
    },
    [dispatch, isSlots],
  );

  const dayOptions = useMemo(() => {
    if (!isSlots) return EMPTY_ARR;
    return (availableSlots || [])
      .map((day) => ({
        date: day.data,
        free: (day.orari || []).filter((o) => o.isBooked == false),
      }))
      .filter((day) => day.free.length > 0)
      .slice(0, 7);
  }, [availableSlots, isSlots]);

  const activeDay =
    dayOptions.find((d) => d.date === slotsDay) || dayOptions[0] || null;

  const promo = {
    title: promoTitle || t('advertising.promo.sampleTitle'),
    discount: promoDiscount,
    validity: promoValidity || t('advertising.promo.sampleValidity'),
  };

  const cacheKey = [
    template.id,
    format,
    workerId,
    activeDay?.date || '',
    selectedReview?.id_recensione || '',
    promoTitle,
    promoDiscount,
    promoValidity,
  ].join('|');

  // Prefetch solo nel modal (una card): in catalogo lo spegniamo.
  const { captureRef, ensureFile, share, sharing } = useStoryShare({
    fileBaseName: `zetabarber-gen-${cacheKey.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 60)}`,
    format,
    isMobile,
    onToast: onSharedToast,
    prefetch: true,
  });

  const workerForCard = selectedWorker
    ? {
        name: getWorkerName(selectedWorker),
        icon: workerIconById[selectedWorker.idpoltrona],
      }
    : null;

  const previewTemplate = isChair
    ? { ...template, kind: 'headline' }
    : template;

  const slotsReady = !isSlots || Boolean(activeDay);

  return (
    <div
      className={styles.modal_backdrop}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        <div className={styles.modal_header}>
          <div>
            <h3 className={styles.modal_title}>{t('advertising.generator.title')}</h3>
            <p className={styles.modal_sub}>{t('advertising.generator.subtitle')}</p>
          </div>
          <button
            type="button"
            className={styles.modal_close}
            onClick={onClose}
            aria-label={t('advertising.actions.close')}
          >
            <X size={18} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <div className={styles.modal_body}>
          <div className={styles.modal_preview}>
            <TemplatePreview
              template={previewTemplate}
              salon={salon}
              captureRef={captureRef}
              square={format === 'post'}
              review={isReview ? selectedReview : null}
              promo={isPromo ? promo : null}
              worker={workerForCard}
              slots={isSlots ? activeDay?.free : null}
              slotsDate={isSlots ? activeDay?.date : null}
              t={t}
            />
          </div>

          {isChair || isSlots ? (
            <div className={styles.field}>
              <label htmlFor="adv-worker">{t('advertising.generator.chair')}</label>
              <select
                id="adv-worker"
                value={workerId}
                onChange={(e) => {
                  setWorkerId(e.target.value);
                  setSlotsDay('');
                }}
              >
                {(workers || []).map((w) => (
                  <option key={w.idpoltrona} value={w.idpoltrona}>
                    {getWorkerName(w) || `Poltrona ${w.idpoltrona}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {isSlots ? (
            dayOptions.length > 0 ? (
              <div className={styles.field}>
                <label htmlFor="adv-day">{t('advertising.generator.day')}</label>
                <select
                  id="adv-day"
                  value={activeDay?.date || ''}
                  onChange={(e) => setSlotsDay(e.target.value)}
                >
                  {dayOptions.map((d) => (
                    <option key={d.date} value={d.date}>
                      {dayjs().isSame(dayjs(d.date), 'day')
                        ? t('scheduler.workerSoldOutModal.today')
                        : dayjs(d.date).format('DD MMMM')}{' '}
                      · {d.free.length} slot
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className={styles.modal_hint}>{t('advertising.generator.loadingSlots')}</p>
            )
          ) : null}

          {isReview ? (
            <div className={styles.review_list}>
              {usableReviews.length === 0 ? (
                <p className={styles.modal_hint}>{t('advertising.reviews.empty')}</p>
              ) : (
                usableReviews.map((r) => {
                  const selected =
                    selectedReview?.id_recensione === r.id_recensione;
                  return (
                    <button
                      key={r.id_recensione}
                      type="button"
                      className={clsx(styles.review_item, {
                        [styles.review_item_selected]: selected,
                      })}
                      onClick={() => setReviewId(r.id_recensione)}
                    >
                      <span className={styles.review_item_text}>{r.testo}</span>
                      <span className={styles.review_item_meta}>
                        {[r.nome, r.cognome].filter(Boolean).join(' ')} ·{' '}
                        {Number(r.rating_average || 0).toFixed(1)}★
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          ) : null}

          {isPromo ? (
            <>
              <div className={styles.field}>
                <label htmlFor="adv-promo-title">{t('advertising.promo.titleLabel')}</label>
                <input
                  id="adv-promo-title"
                  value={promoTitle}
                  onChange={(e) => setPromoTitle(e.target.value)}
                  placeholder={t('advertising.promo.sampleTitle')}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="adv-promo-discount">
                  {t('advertising.promo.discountLabel')}
                </label>
                <input
                  id="adv-promo-discount"
                  value={promoDiscount}
                  onChange={(e) => setPromoDiscount(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="adv-promo-validity">
                  {t('advertising.promo.validityLabel')}
                </label>
                <input
                  id="adv-promo-validity"
                  value={promoValidity}
                  onChange={(e) => setPromoValidity(e.target.value)}
                  placeholder={t('advertising.promo.sampleValidity')}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className={styles.modal_footer}>
          <button type="button" className={styles.btn_secondary} onClick={onClose}>
            {t('advertising.actions.close')}
          </button>
          <button
            type="button"
            className={styles.btn_primary}
            disabled={sharing || !slotsReady}
            onPointerDown={() => {
              if (slotsReady) ensureFile().catch(() => {});
            }}
            onClick={share}
          >
            <Share2 size={16} strokeWidth={2.25} aria-hidden />
            <span>
              {sharing
                ? t('scheduler.workerSoldOutModal.preparing')
                : t('advertising.actions.share')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const FormatToggle = ({ format, onChange, t }) => (
  <div className={styles.format_toggle} role="tablist">
    <button
      type="button"
      role="tab"
      aria-selected={format === 'story'}
      className={clsx(styles.format_btn, {
        [styles.format_btn_active]: format === 'story',
      })}
      onClick={() => onChange('story')}
    >
      <Smartphone size={14} strokeWidth={2.25} aria-hidden />
      <span>{t('advertising.format.story')}</span>
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={format === 'post'}
      className={clsx(styles.format_btn, {
        [styles.format_btn_active]: format === 'post',
      })}
      onClick={() => onChange('post')}
    >
      <ImageIcon size={14} strokeWidth={2.25} aria-hidden />
      <span>{t('advertising.format.post')}</span>
    </button>
  </div>
);

const RegenerateButton = ({ isPremium, onRegenerate, onUpgrade, t }) => {
  if (!isPremium) {
    return (
      <button type="button" className={styles.regen_btn_locked} onClick={onUpgrade}>
        <Flame size={14} strokeWidth={2.4} fill="currentColor" aria-hidden />
        <span>{t('advertising.regenerate')}</span>
      </button>
    );
  }
  return (
    <button type="button" className={styles.regen_btn} onClick={onRegenerate}>
      <RefreshCw size={14} strokeWidth={2.4} aria-hidden />
      <span>{t('advertising.regenerate')}</span>
    </button>
  );
};

const AdvertisingHub = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  // Selettori stabili: getX() crea un createSelector nuovo a ogni chiamata.
  const selectSalon = useMemo(() => getActiveSalon(), []);
  const selectSalonId = useMemo(() => getActiveSalonId(), []);
  const selectReviews = useMemo(() => getReviews(), []);
  const salon = useSelector(selectSalon);
  const salonId = useSelector(selectSalonId);
  const { workers } = useSelector(getCalendarLayoutSource);
  const reviews = useSelector(selectReviews);
  const onSharedToast = useShareToast(t);

  const [category, setCategory] = useState('all');
  // Formato per sezione: { [categoryId]: 'story' | 'post' }
  const [sectionFormats, setSectionFormats] = useState({});
  // Seed shuffle per sezione (Genera nuove)
  const [sectionSeeds, setSectionSeeds] = useState({});
  const [generator, setGenerator] = useState(null); // { template, format }

  const isPremiumUnlocked = useIsPremiumUnlocked();

  const goToUpgrade = useCallback(() => {
    navigate(PREMIUM_UPGRADE_PATH);
  }, [navigate]);

  // Dati calendario (poltrone/servizi) + recensioni, come fa la Home
  useEffect(() => {
    if (!salonId) return;
    const today = dayjs().format('YYYY-MM-DD');
    dispatch(
      schedulerActions.GET_CALENDAR_DATA.REQUEST(
        { startDate: today, endDate: today },
        null,
        SKIP_LOADER,
      ),
    );
    dispatch(reviewsActions.GET_REVIEWS.REQUEST({}, null, SKIP_LOADER));
  }, [dispatch, salonId]);

  const workerIconById = useMemo(() => {
    const map = Object.create(null);
    const list = Array.isArray(workers) ? workers : [];
    for (let i = 0; i < list.length; i += 1) {
      const w = list[i];
      map[w.idpoltrona] = getIconUrl(w.idpoltrona, imageEntities.worker);
    }
    return map;
  }, [workers]);

  // Tutte le sezioni disponibili per questo piano (per chips e render)
  const allSections = useMemo(() => {
    const all = getAllTemplates();
    return CATEGORIES.filter((cat) => cat.id !== 'all')
      .map((cat) => ({
        ...cat,
        templates: all.filter(
          (tpl) =>
            tpl.category === cat.id &&
            resolveTemplateAccess(tpl, isPremiumUnlocked) !== 'hidden',
        ),
      }))
      .filter((cat) => cat.templates.length > 0);
  }, [isPremiumUnlocked]);

  const visibleSections =
    category === 'all'
      ? allSections
      : allSections.filter((section) => section.id === category);

  const chips = useMemo(
    () => [CATEGORIES[0], ...allSections.map(({ id, labelKey }) => ({ id, labelKey }))],
    [allSections],
  );

  return (
    <div className={clsx(styles.root, { [styles.root_mobile]: isMobile })}>
      <div className={styles.page_header}>
        <TitleSectionBack
          title={t('advertising.list.title')}
          description={t('advertising.list.description')}
        />
      </div>

      <div className={styles.categories} role="tablist">
        {chips.map((cat) => (
          <button
            key={cat.id}
            type="button"
            role="tab"
            aria-selected={category === cat.id}
            className={clsx(styles.cat_chip, {
              [styles.cat_chip_active]: category === cat.id,
            })}
            onClick={() => setCategory(cat.id)}
          >
            {t(cat.labelKey)}
          </button>
        ))}
      </div>

      {visibleSections.length === 0 ? (
        <p className={styles.empty}>{t('advertising.empty')}</p>
      ) : (
        visibleSections.map((section) => {
          const sectionFormat = sectionFormats[section.id] || 'story';
          const canRegenerate = REGENERATE_SECTION_IDS.has(section.id);
          const seed = sectionSeeds[section.id] ?? 1;
          const templates = canRegenerate
            ? pickVisibleTemplates(section.templates, seed)
            : section.templates;

          return (
            <section
              key={`${section.id}-${seed}`}
              className={clsx(styles.section, {
                [styles.section_custom]: section.id === 'custom',
              })}
            >
              <div className={styles.section_head}>
                <div className={styles.section_head_text}>
                  <h2 className={styles.section_title}>{t(section.labelKey)}</h2>
                  <p className={styles.section_subtitle}>
                    {t(`advertising.sections.${section.id}`)}
                  </p>
                </div>
                <div className={styles.section_head_actions}>
                  {canRegenerate ? (
                    <RegenerateButton
                      isPremium={isPremiumUnlocked}
                      onUpgrade={goToUpgrade}
                      onRegenerate={() =>
                        setSectionSeeds((prev) => ({
                          ...prev,
                          [section.id]: (prev[section.id] || 1) + 1,
                        }))
                      }
                      t={t}
                    />
                  ) : null}
                  <FormatToggle
                    format={sectionFormat}
                    onChange={(next) =>
                      setSectionFormats((prev) => ({ ...prev, [section.id]: next }))
                    }
                    t={t}
                  />
                </div>
              </div>
              <div className={styles.row}>
                {templates.map((template) => (
                  <TemplateTile
                    key={template.id}
                    template={template}
                    salon={salon}
                    access={resolveTemplateAccess(template, isPremiumUnlocked)}
                    format={sectionFormat}
                    isMobile={isMobile}
                    t={t}
                    onUpgrade={goToUpgrade}
                    onOpenGenerator={(tpl) =>
                      setGenerator({ template: tpl, format: sectionFormat })
                    }
                    onSharedToast={onSharedToast}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {generator ? (
        <GeneratorModal
          template={generator.template}
          salon={salon}
          workers={workers}
          workerIconById={workerIconById}
          reviews={reviews}
          format={generator.format}
          isMobile={isMobile}
          t={t}
          onClose={() => setGenerator(null)}
          onSharedToast={onSharedToast}
        />
      ) : null}
    </div>
  );
};

export default memo(AdvertisingHub);
